import React, { useEffect, useState, useRef } from 'react';
import './style.scss';
import Icon from 'components/Utils/NewIcon';
import ProgressMessage from 'components/Utils/ProgressMessage';
import logo from 'assets/img/logo-white-horizontal.webp';
import permissionsHelp from 'assets/img/permissions-help.png';
import DefaultButton from 'components/Utils/NewDefaultButton';
import Webcam from 'react-webcam';
import { DefaultList, DefaultSwitch } from 'services/UIUtils';
import Draggable from 'react-draggable';
import GenericBadge from 'components/Utils/GenericBadge';
import SelectableListItem from 'components/Utils/SelectableListItem';
import appIcon from 'assets/img/app-icon@180x180.webp';
import Bowser from 'bowser';
import config from 'config';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { videoActions } from 'careand-redux/slices/ui/video';
import { usePathParams } from 'components/Navigation/NavStack';
import LoadingScreen from 'components/Utils/LoadingScreen';
import JitsiMeet from 'components/Video/Jitsi/JitsiMeet';
import { useAppSelector } from 'careand-redux/store';
import MiscUtils from 'services/MiscUtils';
import moment from 'moment-timezone';
import { STUDENT_CONSENT_TEXT } from 'scenes/Questionnaire';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import { Device } from '../../services/Native/Device';
import { Stacks } from '../../careand-redux/slices/ui/navigation';
import { useNavTab, useNavStack } from '../../careand-redux/slices/ui/hooks';

const CONNECT_ERROR =
  'This appointment is not available at this time. Please make sure you are using the correct link.';

const Telemedicine = ({
  id,
  isMobile = false,
  onDisconnected,
  onClose,
  isPhysician = false,
  saveTo,
  appointmentId,
}) => {
  const dispatch = useAppDispatch();

  const ownUser = useAppSelector((state) => state.authentication.user);
  const ownId = ownUser?.id;

  const [error, setError] = useState();
  const [defaults, setDefaults] = useState();
  const [meetingData, setMeetingData] = useState();
  const warningTimerRef = useRef(null);

  const endDate = meetingData?.appointment?.date_end;

  useEffect(() => {
    if (id) {
      dispatch(api.Video.joinVideoAppointment2({ appointmentId: id })).then((result) => {
        if (result) {
          setMeetingData(result);
        } else {
          setError(CONNECT_ERROR);
        }
      });
    }
  }, [dispatch, id]);

  useEffect(() => {
    //for authenticated users, they will receive a warning from the backend directly
    if (!ownId && endDate) {
      const endTime = moment.unix(endDate).seconds(0);
      const fiveMinutesBeforeEnd = moment(endTime).subtract(5, 'minutes');
      const now = moment();
      const timeUntilWarning = fiveMinutesBeforeEnd.diff(now, 'millisecond');
      const message = 'Friendly reminder, you have about 5 minutes left in your appointment.';

      if (timeUntilWarning > 0) {
        warningTimerRef.current = setTimeout(() => {
          MiscUtils.showToastrInfo({ message });
        }, timeUntilWarning);
      }
    }

    return () => {
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
    };
  }, [endDate, ownId]);

  async function endMeeting() {
    if (isPhysician) {
      let callLog = {
        status: 'ended',
        appointment_id: id,
        patient_id: saveTo,
        physician_id: ownUser.id,
        type: 'video',
      };
      await dispatch(api.Resource.submitCallLog({ callLog }));
    }
  }

  async function connectedToTelemedicine(participants) {
    await dispatch(api.Video.connectedToTelemedicine({ appointmentId: id, participants }));
  }

  function onStartConnection(defaults) {
    setDefaults(defaults);
  }

  return (
    <div className="telemedicine">
      <div className="content">
        {!!defaults ? (
          <JitsiMeet
            saveTo={saveTo}
            isPhysician={isPhysician}
            defaults={defaults}
            meetingData={meetingData}
            onConnected={connectedToTelemedicine}
            appointmentId={appointmentId}
            onDisconnected={() => {
              setDefaults();
              endMeeting();
              if (onDisconnected) onDisconnected();
            }}
          />
        ) : null}
        {!defaults && (
          <>
            {!!onClose && (
              <Icon
                alt="Close"
                src="xmark"
                showTooltip={true}
                className="close-button white"
                onClick={onClose}
              />
            )}
            <div className="non-video-wrapper">
              <WaitScreen
                error={error}
                showLoading={!meetingData && !error}
                onStartConnection={onStartConnection}
                isMobile={isMobile}
                id={id}
                meetingData={meetingData}
                isPhysician={isPhysician}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const TelemedicineModal = ({ id, saveTo, appointmentId }) => {
  const dispatch = useAppDispatch();

  function onDisconnected() {
    dispatch(videoActions.openVideoScreen(undefined));
  }

  return (
    <Draggable key="123" bounds="body">
      <div className="new-version-video">
        <Telemedicine
          id={id}
          onDisconnected={onDisconnected}
          onClose={onDisconnected}
          isPhysician={true}
          saveTo={saveTo}
          appointmentId={appointmentId}
        />
      </div>
    </Draggable>
  );
};

export const TelemedicinePage = () => {
  let id = usePathParams().id;

  const [mobile] = useState(mobileCheck());

  function setDocHeight() {
    document.documentElement.style.setProperty('--vh', `${window.innerHeight / 100}px`);
  }

  window.addEventListener('resize', () => setDocHeight());
  window.addEventListener('orientationchange', () => setDocHeight());
  setDocHeight();

  function onDisconnected() {
    if (mobile) window.location.reload();
  }

  return (
    <div className={'telemedicine-page' + (mobile ? ' mobile' : '')}>
      <Telemedicine
        id={id}
        isMobile={mobile}
        fullScreen={true}
        isPhysician={false}
        onDisconnected={onDisconnected}
      />
    </div>
  );
};

const STATES = {
  initialLoading: 'initialLoading',
  acceptMobile: 'acceptMobile',
  askPermissions: 'askPermissions',
  configureSetup: 'configureSetup',
};

const WaitScreen = ({
  error,
  onStartConnection,
  showLoading,
  isMobile,
  id,
  meetingData,
  isPhysician,
}) => {
  const masterNav = useNavStack(Stacks.Master);
  const bookingNav = useNavStack(Stacks.Appointments);
  const navTab = useNavTab();
  const [state, setState] = useState(STATES.initialLoading);

  useEffect(() => {
    if (!showLoading) {
      if (Device.isMobile()) {
        bookingNav.popToRoot();
        masterNav.popToRoot();
        navTab.switchTab('appointments');
        return;
      }
      if (isMobile) {
        setState(STATES.acceptMobile);
      } else {
        setState(STATES.askPermissions);
      }
    }
  }, [showLoading, isMobile]);

  return (
    <>
      {showLoading && <LoadingScreen hide={false} />}
      {!showLoading && (
        <div className="wait-screen">
          <Logo />
          {!error && (
            <>
              {state === STATES.acceptMobile && (
                <AcceptMobileScreen onAccept={() => setState(STATES.askPermissions)} id={id} />
              )}
              {state === STATES.askPermissions && (
                <PermissionsScreen onSetup={() => setState(STATES.configureSetup)} />
              )}
              {state === STATES.configureSetup && (
                <ConfigurationScreen
                  onJoin={onStartConnection}
                  meetingData={meetingData}
                  id={id}
                  isPhysician={isPhysician}
                />
              )}
            </>
          )}
          {!!error && (
            <div className="mt-10 block">
              <div className="error">{error}</div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

const AcceptMobileScreen = ({ id, onAccept }) => {
  const icon = getBrowserIcon();
  const storeIcon = getStoreIcon();

  function onClickBrowser() {
    onAccept();
  }

  function onClickApp() {
    let url = 'careand://video/call?id=' + id;
    if (config.ENVIRONMENT !== 'production') url = `staging${url}`;
    window.location = url;
  }

  function onClickDownload() {
    if (storeIcon.name === 'App Store') {
      window.location = 'https://apps.apple.com/ca/app/homemd/id1299088137';
    } else if (storeIcon.name === 'Play Store') {
      window.location = 'https://play.google.com/store/apps/details?id=ca.homemd.homemd';
    }
  }

  return (
    <div className="accept-mobile-screen">
      <span className="text mt-10 mb-5 block white-text">
        How do you want to join your telemedicine appointment?
      </span>

      <div className="flex flex-col mx-auto" style={{ maxWidth: '100%', width: '400px' }}>
        <DefaultList>
          <SelectableListItem
            divider={false}
            onClick={onClickBrowser}
            item={
              <GenericBadge
                avatarVariant="square"
                avatar={<Icon fontAwesome={icon.src} fontAwesomeStyle={icon.style} height="40px" />}
                title="Join using this browser"
                info="No download or installation required"
              />
            }
          />
          <SelectableListItem
            divider={false}
            onClick={onClickApp}
            item={
              <GenericBadge
                avatarVariant="square"
                avatar={<img alt="Care&" src={appIcon} />}
                title="Open your Care& app"
                info="Already have it? Join using the app"
              />
            }
          />
          {!!storeIcon.src && (
            <SelectableListItem
              divider={false}
              onClick={onClickDownload}
              item={
                <GenericBadge
                  avatarVariant="square"
                  avatar={
                    <Icon
                      fontAwesome={storeIcon.src}
                      fontAwesomeStyle={storeIcon.style}
                      height="40px"
                    />
                  }
                  title="Install the Care& app"
                  info="Don't have it? Install it here"
                />
              }
            />
          )}
        </DefaultList>
      </div>
    </div>
  );
};

const ConfigurationScreen = ({ onJoin, meetingData, id, isPhysician }) => {
  const dispatch = useAppDispatch();
  const videoRef = useRef();
  const [audioOn, setAudioOn] = useState(true);
  const [videoOn, setVideoOn] = useState(true);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [submittingConsent, setSubmittingConsent] = useState(false);

  const handleJoinClick = () => {
    const shouldShowConsentModal =
      !isPhysician && meetingData?.questionnaire_code && meetingData?.has_student;
    if (shouldShowConsentModal) {
      setShowConsentModal(true);
    } else {
      onJoin({ audio: audioOn, video: videoOn });
    }
  };

  const handleConsentSubmit = async (consent) => {
    setSubmittingConsent(true);

    const answer = consent ? 'Yes' : 'No';

    await dispatch(
      api.Appointments.submitPortalQuestionnaire({
        id,
        code: meetingData?.questionnaire_code,
        questionnaire: {
          questions: [
            {
              question: STUDENT_CONSENT_TEXT,
              answer,
            },
          ],
        },
      }),
    );

    setSubmittingConsent(false);
    setShowConsentModal(false);

    onJoin({ audio: audioOn, video: videoOn });
  };

  return (
    <div className="configuration-screen">
      <span className="text white-text">
        Choose your audio and video settings and click "Join Appointment" below when you're ready.
      </span>

      <div className="webcam-wrapper">
        <div className="webcam-top" />
        {videoOn ? <Webcam audio={false} ref={videoRef} mirrored={true} /> : null}
      </div>

      <div className="controls">
        <div className="row">
          <DefaultSwitch
            name="microphone"
            onChange={(event, isInputChecked) => setAudioOn(isInputChecked)}
            label={<Icon src="microphone" alt="Microphone" width="15px" />}
            checked={audioOn}
          />
          <DefaultSwitch
            name="camera"
            onChange={(event, isInputChecked) => setVideoOn(isInputChecked)}
            label={<Icon src="video" alt="Webcam" width="20px" />}
            checked={videoOn}
          />
        </div>
        <div className="row">
          <DefaultButton
            name="Join Appointment"
            className="blue"
            style={{ width: '200px' }}
            onClick={handleJoinClick}
          />
        </div>
      </div>

      {showConsentModal && (
        <ConfirmationDialog
          title="Student Consent"
          message={STUDENT_CONSENT_TEXT}
          open={showConsentModal}
          onConfirm={() => handleConsentSubmit(true)}
          onCancel={() => handleConsentSubmit(false)}
          looadingState={submittingConsent}
          confirmLabel="Yes, I consent"
          cancelLabel="No"
        />
      )}
    </div>
  );
};

const PermissionsScreen = ({ onSetup }) => {
  const [hasWebcamPermissions, setHasWebcamPermissions] = useState(undefined);
  const [hasMicrophonePermissions, setHasMicrophonePermissions] = useState(undefined);
  const [error, setError] = useState(false);

  useEffect(() => {
    // checkPermissions();
    let constraints = { video: true, audio: true };
    navigator.mediaDevices
      .getUserMedia(constraints)
      .then((it) => {
        checkPermissions();
      })
      .catch((err) => {
        console.log(err);
        setError(true);
      });
  }, []);

  async function checkPermissions() {
    let devices = await navigator.mediaDevices.enumerateDevices();

    let video = devices.find(({ kind }) => kind === 'videoinput');
    let webcamPermissions = !!video && video.deviceId;
    setHasWebcamPermissions(webcamPermissions);

    let audio = devices.find(({ kind }) => kind === 'audioinput');
    let microphonePermissions = !!audio && audio.deviceId;
    setHasMicrophonePermissions(microphonePermissions);

    if (!webcamPermissions || !microphonePermissions) {
      let constraints = { video: !webcamPermissions, audio: !microphonePermissions };
      try {
        let stream = await navigator.mediaDevices.getUserMedia(constraints);
        stream.getTracks().forEach((track) => track.stop());
        onSetup();
      } catch (err) {
        console.log(err);
        setError(true);
      }
    } else {
      onSetup();
    }
  }

  const hasChecked = hasWebcamPermissions !== undefined && hasMicrophonePermissions !== undefined;
  const needsToAsk = !hasWebcamPermissions || !hasMicrophonePermissions;

  return (
    <div className="permissions-screen">
      {error && (
        <>
          <div className="text">
            Are you sure you don't want video or audio? If you change your mind, select the camera
            icon by your address bar and then{' '}
            <b>
              <u>Always allow</u>
            </b>
            .
            <img alt="Setting up permissions" src={permissionsHelp} width={300} />
          </div>
          <DefaultButton
            name="Continue without video or audio"
            onClick={onSetup}
            style={{ width: '300px' }}
          />
        </>
      )}
      {hasChecked && needsToAsk && !error && (
        <div className="text">
          <span className="large">
            Select{' '}
            <b>
              <u>Allow</u>
            </b>{' '}
            to let Care& Family Health use your camera and microphone for telemedicine appointments
            on this browser.
          </span>
          <br />
          <span className="pt-2 block">
            When you do, we'll turn your devices on for a moment to set them up.
          </span>
        </div>
      )}
    </div>
  );
};

const Logo = ({ loading = false, width = 230 }) => {
  return (
    <div className={'logo-wrapper ' + (loading ? ' loading' : '')}>
      <img alt="Care& Family Health" src={logo} width={width} />
      <ProgressMessage size={280} thickness={1} show={loading} />
    </div>
  );
};

function getStoreIcon() {
  const browser = Bowser.getParser(window.navigator.userAgent);
  let { name } = browser.getOS();
  name = name.toLowerCase();

  switch (name) {
    case 'ios':
      return { name: 'App Store', style: 'fab', src: 'app-store-ios' };
    case 'android':
      return { name: 'Play Store', style: 'fab', src: 'android' };
    default:
      return {};
  }
}

function getBrowserIcon() {
  const browser = Bowser.getParser(window.navigator.userAgent);
  const { name } = browser.getBrowser();

  switch (name) {
    case 'Opera':
      return { style: 'far', src: 'window-maximize' };
    case 'Firefox':
      return { style: 'fab', src: 'firefox-browser' };
    case 'Internet Explorer':
      return { style: 'fab', src: 'internet-explorer' };
    case 'Edge':
      return { style: 'fab', src: 'edge' };
    case 'Safari':
      return { style: 'fab', src: 'safari' };
    case 'Chrome':
      return { style: 'fab', src: 'chrome' };
    default:
      return { style: 'far', src: 'window-maximize' };
  }
}

export function mobileCheck() {
  let check = false;
  (function (a) {
    // eslint-disable-next-line no-useless-escape
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a,
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4),
      )
    )
      check = true;
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
}

export default Telemedicine;
